import React, { useState, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useCookies } from 'react-cookie'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import config from 'environment'

import { defaultCategories, STACK, VIEW } from 'types/deck'

import generateInitialState from 'redux/cacheables/initialState'
import { ReduxServerProps } from 'redux/store'
import { useActions, useActionless, useAppSelector } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { generateInitialDeckState } from 'redux/deck/initialState'
import { setDeckStateAction } from 'redux/deck/actions'
import { selectCommanderCards, selectStacks, useCanEditDeck } from 'redux/deck/selectors'

import WebsocketService from 'services/websocket.service'
import CookieService from 'services/cookie.service'
import LocalStorageService from 'services/localStorage.service'
import { DeckService } from 'services/deck.service'
import { CardService } from 'services/card.service'
import { getControlledStackOptions, getSorting, getStack } from 'services/accountSettings.service'

import PageWrapper from 'components/misc/PageWrapper'
import SandboxMessage from 'components/deckPage/misc/SandboxMessage'
import FilteredViewMessage from 'components/deckPage/misc/FilteredViewMessage'
import EmptyDeckLanding from 'components/deckPage/misc/EmptyDeckLanding'
import CustomAd from 'components/advertisments/CustomAd'
import InlineAd from 'components/advertisments/InlineAd'
import TokenContainer from 'components/deckPage/tokens/TokenContainer'
import DescriptionContainer from 'components/deckPage/DescriptionContainer'
import StatsContainer from 'components/deckPage/StatsContainer'
import CommentContainer from 'components/deckPage/CommentContainer'
import DeckHeader from 'components/deckPage/header/DeckHeader'
import DeckFilters from 'components/deckPage/filtersAndControls/FilterBar'
import CardDragLayer from 'components/deckPage/dragAndDrop/CardDragLayer'
import NewCategoryDropZoneModal from 'components/deckPage/modals/NewCategoryDropZoneModal'
import DeckSettingsOverlay from 'components/deckPage/settings/DeckSettingsOverlay'
import ImportOverlay from 'components/deckPage/modals/Import'
import ExportModal from 'components/deckPage/modals/Export'
import HistoryModal from 'components/deckPage/deckHistory/historyModal'
import DeckKeybindsOverlay from 'components/deckPage/modals/KeybindsOverlay'
import DeckPageContextMenu from 'components/deckPage/misc/ContextMenu'
import DragDropProvider from 'components/misc/DragDropProvider'
// import CommandersHeraldArticles from 'components/advertisments/CommandersHeraldArticles'
import ImportDropWrapper from 'components/misc/ImportDropWrapper'
import DeckLayoutPresenter from 'components/deckPage/DeckLayoutPresenter'
import DeckContainerV2 from 'components/deckPage/DeckContainerV2'
import CopyDeck from 'components/deckPage/modals/CopyDeck'
import PackageSelectionOverlay from 'components/deckPage/modals/cardPackages/SelectionOverlay'
import AddToCollectionModal from 'components/deckPage/modals/AddToCollection'
import FixedDropZones from 'components/deckPage/misc/FixedDropZones'
import AutofillBasicLandsOverlay from 'components/deckPage/modals/autofillBasicLands'

import OptimizeCards from 'components/deckPage/modals/OptimizeCards'
import NewSnapshotModalStandalone from 'components/deckPage/deckHistory/newSnapshotModalStandalone'
import BuyBulkModal from 'components/deckPage/modals/BuyBulk'
import Icon from 'components/elements/Icon'
import RightPanelV2 from 'components/deckPage/RightPanel'
import MobileToolbar from 'components/deckPage/filtersAndControls/MobileToolbar'
import FloatingMenu from 'components/misc/FloatingMenu'
import { GlobalCateogoryModal } from 'components/deckPage/categories/Modal'

import { generateDeckDescription, generateDeckUrl } from 'utils/deckSeo'
import { calculateSort, calculateStack } from 'utils/queryParams/deck.queryParams'
import { deckResponseToDeckState } from 'utils/responseToDeck'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'
import { getDeckFromUrl } from 'utils/deckAsQueryParam'

import { useComponentDidMount } from 'hooks/componentDidMount'

import { descriptionToTop } from 'components/accountSettingsPage/BrowserSpecificSiteSettings'
import { emptyTextAreaString } from 'types/editor'

import styles from './decks.module.scss'

export type Props = { loadedWithForcedPerspectiive?: boolean; ckAffiliate?: boolean; tcgAffiliate?: boolean }

const DeckPage = ({ loadedWithForcedPerspectiive = false, ckAffiliate = false, tcgAffiliate = false }: Props) => {
  const router = useRouter()
  const componentIsMounted = useComponentDidMount()

  const [setDeckState] = useActions(setDeckStateAction)
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const [{ tbId: userId, [descriptionToTop]: descriptionAboveDeck }] = useCookies(['tbId', descriptionToTop])

  const deckId = useAppSelector(state => state.deck.id)
  const canEditDeck = useCanEditDeck()
  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)
  const view = useAppSelector(state => state.deck.view)
  const stack = useAppSelector(state => state.deck.stack)
  const controlledStackOptions = useAppSelector(state => state.deck.controlledStackOptions)
  const name = useAppSelector(state => state.deck.name)
  const format = useAppSelector(state => state.deck.format)
  const description = useAppSelector(state => state.deck.description)
  const featured = useAppSelector(state => state.deck.featured)
  const customFeatured = useAppSelector(state => state.deck.customFeatured)
  const deckTags = useAppSelector(state => state.deck.deckTags)
  const commanders = useAppSelector(selectCommanderCards)
  const stacks = useAppSelector(selectStacks)
  const ownerId = useAppSelector(state => state.deck.ownerid)
  const optimizeCardOverlay = useAppSelector(state => state.active.optimizeCardOverlay)
  const autofillBasicLandsOverlay = useAppSelector(state => state.active.autofillBasicLandsOverlay)
  const disableMargins = useAppSelector(state => state.cacheables.disableMargins)
  const priceSource = useAppSelector(state => state.active.priceSource)

  const [loading, setLoading] = useState(true)
  const [forcedPerspective, setForcePerspective] = useState(loadedWithForcedPerspectiive)

  const sandbox = !deckId
  const filtered = !!router.query?.filter

  const resetSorting = () => {
    const sorting = getSorting()
    const stack = getStack()

    setDeckState({ sorting, stack })
    setForcePerspective(false)
  }

  useEffect(() => {
    const sandboxCachedDeck = LocalStorageService.getObject('sandboxDeck')

    if (sandbox && sandboxCachedDeck) {
      LocalStorageService.remove('sandboxDeck')

      setDeckState(sandboxCachedDeck)
    }

    if (canEditDeck) CookieService.set('tbBanner', customFeatured || featured, 'month')

    if (!sandbox) WebsocketService.initialize(deckId)

    if (config.getIsFirefox()) document.body.style.overflow = 'auto'

    const cleanDeckState = generateInitialDeckState()

    const urlSort = `${router.query.sort || ''}`
    const urlStack = `${router.query.stack || ''}`

    const sorting = urlSort ? calculateSort(urlSort) : null
    const stack = urlStack ? calculateStack(urlStack) : null

    ReactGA.event({ category: 'Deck', action: `loaded with group by ${cleanDeckState.stack}` })
    ReactGA.event({ category: 'Deck', action: `loaded with view as ${cleanDeckState.view}` })

    ReactGA.event({ category: 'Deck', action: `loaded with price source primary as ${priceSource[0]}` })
    ReactGA.event({ category: 'Deck', action: `loaded with price source secondary as ${priceSource[1]}` })

    ReactGA.event({ category: 'Deck', action: `deck page loaded logged in ${!!userId}` })
    ReactGA.event({ category: 'Deck', action: `deck page loaded owned ${`${ownerId}` === `${userId}`}` })

    // TODO - is there a way to move this up in the render cycle?
    const controlledStackOptions = getControlledStackOptions(deckId)

    setDeckState({
      controlledStackOptions,
      stack: stack || cleanDeckState.stack,
      view: cleanDeckState.view,
      sorting: sorting || cleanDeckState.sorting,
    })

    setTimeout(() => setLoading(false), 200)

    return () => {
      setActiveState({ searchOpen: false })

      WebsocketService.uninitialize()
    }
  }, [])

  useEffect(() => {
    const isControlledStacks =
      view === VIEW.CARD && controlledStackOptions?.controlled && (stack === STACK.CUSTOM || stack === STACK.MULTIPLE)

    if (view === VIEW.SPREADSHEET || isControlledStacks) document.body.classList.add('deck-overscroll-view')
    if (isControlledStacks) document.body.classList.add('no-deck-margins')

    return () => {
      document.body.classList.remove('deck-overscroll-view')
      document.body.classList.remove('no-deck-margins')
    }
  }, [view, controlledStackOptions, stack])

  const { title, description: metaDescription } = generateDeckDescription(
    name,
    format,
    description,
    deckTags,
    commanders,
    stacks,
  )

  const moveDescriptionToTop = `${descriptionAboveDeck}` === 'true' && description !== emptyTextAreaString

  return (
    <ImportDropWrapper>
      <DragDropProvider delayTouchStart={250}>
        <PageWrapper lockable dndPage noSearch title={title} description={metaDescription}>
          <div className={styles.page}>
            <DeckHeader />

            {componentIsMounted && (
              <div className={styles.content}>
                {moveDescriptionToTop && (
                  <div className={styles.tableViewStickyWrapperLower}>
                    <div className={styles.tableViewStickyContainers}>
                      <div className={styles.innerStickyContainer}>
                        <DescriptionContainer className={styles.aboveDescription} />
                      </div>
                    </div>
                  </div>
                )}

                <DeckPageContextMenu className={styles.fullWidth}>
                  <div className={styles.tableViewStickyWrapper}>
                    <div className={styles.tableViewStickyContainers}>
                      <div className={styles.innerStickyContainer}>
                        {sandbox && <SandboxMessage />}
                        {filtered && <FilteredViewMessage />}

                        <InlineAd id="deck-top" leaderboard banner longBanner removeOnNoAd renderVisibleOnly={false} />

                        {snapshotMeta && (
                          <div className={styles.originalDeckLink}>
                            <a href={generateDeckUrl(snapshotMeta.parentDeckId, snapshotMeta.parentDeckName)}>
                              <Icon name="arrow left" /> View current deck - {snapshotMeta.parentDeckName}
                            </a>
                          </div>
                        )}

                        {forcedPerspective && !!userId && (
                          <div className={styles.perspectiveContainer}>
                            <div className={styles.forcePerspective}>
                              This deck has been linked with a specific layout and may be overwriting your user
                              settings. This is usually done because the deck creator wants to display the deck in a
                              certain way. You can still&nbsp;
                              <button className={styles.revertButton} onClick={resetSorting}>
                                revert
                              </button>{' '}
                              the settings at any time.
                              <button
                                className={styles.closePerspectiveButton}
                                onClick={() => setForcePerspective(false)}>
                                <Icon name="remove" />
                              </button>
                            </div>
                          </div>
                        )}

                        <DeckFilters />
                        <DeckLayoutPresenter />
                      </div>
                    </div>
                  </div>

                  <div className={styles.primaryContent} style={{ maxWidth: disableMargins ? 'unset' : undefined }}>
                    <DeckContainerV2 />
                    <RightPanelV2 />
                  </div>
                </DeckPageContextMenu>

                <div className={styles.tableViewStickyWrapperLower}>
                  <div className={styles.tableViewStickyContainers}>
                    <div className={styles.innerStickyContainer}>
                      <CustomAd />
                      <StatsContainer />
                      <TokenContainer />
                      {/* <CommandersHeraldArticles /> */}
                      {!sandbox && !moveDescriptionToTop && <DescriptionContainer />}
                      <InlineAd
                        id="deck-bottom"
                        banner
                        longBanner
                        billboard
                        renderVisibleOnly={false}
                        allowTallMobile
                      />
                      {!sandbox && <CommentContainer />}
                      <CardDragLayer />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <>
            <MobileToolbar />
            <FloatingMenu />
            <EmptyDeckLanding ownsDeck={canEditDeck} />

            {!loading && (
              <>
                <GlobalCateogoryModal />
                <NewCategoryDropZoneModal />
                <DeckSettingsOverlay />
                <ImportOverlay />
                <CopyDeck />
                <ExportModal />
                <HistoryModal />
                <NewSnapshotModalStandalone />

                {/* <MultiSelectControls /> */}
                <DeckKeybindsOverlay />
                <PackageSelectionOverlay />
                <AddToCollectionModal />
                <BuyBulkModal />
                <OptimizeCards
                  open={optimizeCardOverlay}
                  onClose={() => setActiveState({ optimizeCardOverlay: false })}
                />
                <AutofillBasicLandsOverlay
                  open={autofillBasicLandsOverlay}
                  onClose={() => setActiveState({ autofillBasicLandsOverlay: false })}
                />
              </>
            )}
          </>
        </PageWrapper>
        <FixedDropZones />
      </DragDropProvider>
    </ImportDropWrapper>
  )
}

export default DeckPage

export const getServerSideProps: GetServerSideProps<Props & ReduxServerProps> = async context => {
  try {
    let deckId: string = ''

    let articleSlug = ''
    let listSlug = ''
    let articleSource = ''

    if (context.params?.id && typeof context.params?.id === 'string') deckId = context.params.id
    if (Array.isArray(context.params?.deckInfo) && context.params?.deckInfo[0]) deckId = context.params.deckInfo[0]

    if (context.params?.article && typeof context.params?.article === 'string') articleSlug = context.params.article
    if (context.params?.list && typeof context.params?.list === 'string') listSlug = context.params.list
    if (context.params?.source && typeof context.params?.source === 'string') articleSource = context.params.source

    const commanderAs = context.query.commanderAs
    const snapshotPage = context.resolvedUrl?.includes('snapshot')
    const sandbox = context.resolvedUrl?.startsWith('/sandbox')

    const cardService = new CardService(context)
    const cleanDeckState = generateInitialDeckState(context)
    const cacheableState = generateInitialState(context.req.cookies)

    const deckAsUrl = context.query.deck ? `${context.query.deck}` : null
    const sorting = typeof context.query.sort === 'string' ? calculateSort(context.query.sort) : null
    const stack = typeof context.query.stack === 'string' ? calculateStack(context.query.stack) : null

    if (sorting !== null) cleanDeckState.sorting = sorting
    if (stack !== null) cleanDeckState.stack = stack

    const props: Props = {
      loadedWithForcedPerspectiive: !!sorting || !!stack,
      ckAffiliate: false,
      tcgAffiliate: false,
    }

    if (!deckId && !commanderAs && !deckAsUrl && !(articleSlug && listSlug && articleSource)) return { props } // Base sandbox without other optionss
    if (!deckId && commanderAs) {
      const cards = await cardService.listCardsOnly({ name: commanderAs })
      const card = cards[0]

      if (!card) return { props } // couldn't find it, no big, just drop them in the sandbox

      return {
        props: {
          ...props,
          redux: {
            cacheables: cacheableState,
            deck: {
              ...cleanDeckState,
              cardMap: { [card.id]: { ...card, qty: 1, categories: ['Commander'] } },
              categories: {
                ...defaultCategories,
                Commander: { name: 'Commander', isPremier: true, includedInDeck: true, includedInPrice: true },
              },
            },
          },
        },
      }
    }

    let deck

    if (snapshotPage) deck = await new DeckService(context).getSnapshot(deckId)
    // prettier-ignore
    else if (articleSlug && listSlug && articleSource) deck = await new DeckService(context).getArticleDeck(articleSource, articleSlug, listSlug)
    else if (sandbox && deckAsUrl) deck = await getDeckFromUrl(deckAsUrl, context)
    else deck = await new DeckService(context).details(deckId)

    const mergedDeckStates = deckResponseToDeckState(cleanDeckState, deck)

    if (mergedDeckStates.ckAffiliate) props.ckAffiliate = true
    if (mergedDeckStates.tcgAffiliate) props.tcgAffiliate = true

    // prettier-ignore
    if (deck.owner.referrerEnum) CookieService.setServerCookie({ 'referred-by': deck.owner.referrerEnum }, context, 60 * 60 * 24)

    return { props: { ...props, redux: { deck: mergedDeckStates, cacheables: cacheableState } } }
  } catch (err) {
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}
